import { useCallback, useMemo, useRef } from "react";

/**
 * 브라우저 음성인식(Web Speech API) 얇은 감싸개.
 *
 * **지원이 갈린다.** 크롬(데스크톱·안드로이드)과 사파리(iOS 14.5+)는 되고
 * 파이어폭스는 안 된다. 카카오톡 같은 앱 안의 브라우저는 되는 곳도 안 되는 곳도
 * 있다. 우리 학생은 알림톡 링크로 들어오므로 앱 안의 브라우저가 흔하다.
 * 그래서 `isSupported` 가 거짓이면 화면에서 이 기능을 아예 감춘다. 막힌 버튼을
 * 보여 주지 않는다.
 *
 * **`listen()` 은 반드시 사용자가 누른 자리에서 불러야 한다.** iOS 는 마이크를
 * 여는 것을 사용자 동작에 묶어 두어서, 타이머나 재생 루프 안에서 스스로 시작하면
 * 조용히 막힌다.
 *
 * 인식 결과는 구글·애플 서버를 거친다. 소리를 우리가 저장하지는 않지만 외부로
 * 나가는 것은 맞다.
 */
// 아이가 안 끝내도 이만큼 지나면 우리가 끊는다. 길게 두면 말이 끝나고도 한참
// 기다리게 되어 채점이 늦다.
//
// **부르는 쪽이 `maximumMilliseconds` 로 늘릴 수 있다.** 통역처럼 아이가 길게 말하는
// 자리에서는 이 기본값으로는 말하는 중에 끊긴다.
const MAXIMUM_LISTEN_MILLISECONDS = 10000;



export const useSpeechRecognition = () => {
  const recognitionRef = useRef(null);

  const SpeechRecognitionClass = useMemo(
    () =>
      typeof window === "undefined"
        ? null
        : window.SpeechRecognition || window.webkitSpeechRecognition || null,
    []
  );

  const isSupported = Boolean(SpeechRecognitionClass);

  /**
   * 듣기를 **정상 종료**한다. 여기까지 들은 것을 결과로 준다.
   *
   * `stopListening()`(abort)과 다르다. abort 는 들은 것을 버리므로 아이가 다 읽고
   * 눌렀을 때 쓰면 점수가 0 이 된다. 아이가 "다 읽었어요" 로 누르는 것은 이쪽이다.
   */
  const finishListening = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return false;
    try {
      recognition.stop();
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const stopListening = useCallback(() => {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (!recognition) return;
    try {
      recognition.abort();
    } catch (error) {
      // 이미 끝난 뒤의 abort 는 예외를 던진다. 할 일이 없다.
    }
  }, []);

  /**
   * 한 번 듣고 받아쓴 것을 돌려준다.
   *
   * 항상 resolve 한다(거절하지 않는다). 못 알아들었으면 transcript 가 빈 문자열이고
   * errorCode 에 이유가 담긴다. 실패를 예외로 올리면 호출부가 매번 감싸야 하는데,
   * 여기서는 못 알아듣는 것이 정상 흐름의 하나다.
   */
  const listen = useCallback(
    ({
      language = "en-US",
      onSpeechActivity = null,
      continuous = false,
      maximumMilliseconds = MAXIMUM_LISTEN_MILLISECONDS,
    } = {}) =>
      new Promise((resolve) => {
        if (!SpeechRecognitionClass) {
          resolve({ transcript: "", elapsedMilliseconds: 0, errorCode: "unsupported" });
          return;
        }

        stopListening();

        const recognition = new SpeechRecognitionClass();
        recognition.lang = language;
        // **이어 듣기(continuous)를 쓰지 않는다.** 켜면 말이 끝나도 스스로 끝내지
        // 않아 상한까지 기다리게 되고(채점이 한참 늦다), 다시 열 때마다 iOS 가
        // '띠링' 소리를 낸다. 대신 한 번에 한 문장을 받는다.
        recognition.continuous = continuous;
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognitionRef.current = recognition;

        // 말이 멈춰도 스스로 끝내지 않는 단말이 있다. 상한을 두고 우리가 끊는다.
        const guardTimer = setTimeout(() => {
          try {
            recognition.stop();
          } catch (error) {
            // 이미 끝났으면 할 일이 없다.
          }
          // stop() 에도 onend 가 안 오는 단말이 있어 한 번 더 받쳐 준다.
          setTimeout(() => {
            errorCode = errorCode || "no_end";
            settle();
          }, 1500);
        }, maximumMilliseconds);

        const startedAt = performance.now();
        let transcript = "";
        let errorCode = null;
        let isSettled = false;

        const settle = () => {
          if (isSettled) return;
          isSettled = true;
          clearTimeout(guardTimer);
          recognitionRef.current = null;
          resolve({
            transcript,
            elapsedMilliseconds: Math.round(performance.now() - startedAt),
            errorCode,
          });
        };

        // 시작 신호를 따로 감시하지 않는다. iOS 사파리는 인식이 멀쩡히 돌아도
        // onstart/onaudiostart 를 안 쏘는 일이 있어서, 그것을 근거로 끊었더니
        // **멀쩡한 인식을 2.5초 만에 우리 손으로 죽이고 있었다.** '받아쓰기 꺼짐'
        // 으로 영영 안 끝나는 경우는 아래 12초 상한이 받는다.
        // 말소리가 잡히기 시작했다/끝났다. 마이크를 따로 열지 않고도 화면의 음파
        // 표시를 실제 소리에 맞춰 움직일 수 있는 유일한 신호다(마이크를 또 열면
        // iOS 에서 인식이 깨진다).
        recognition.onspeechstart = () => {
          if (onSpeechActivity) onSpeechActivity(true);
        };
        recognition.onspeechend = () => {
          if (onSpeechActivity) onSpeechActivity(false);
        };

        recognition.onresult = (event) => {
          // 이어 듣기에서는 결과가 여러 번 나뉘어 온다. 확정된 것만 모은다.
          transcript = Array.from(event.results)
            .filter((result) => result.isFinal !== false)
            .map((result) => result[0]?.transcript || "")
            .join(" ")
            .replace(/\s+/g, " ")
            .trim();
        };

        recognition.onerror = (event) => {
          errorCode = event.error || "unknown";
          // onerror 뒤에 onend 가 안 오는 단말이 있다. 잠깐 기다렸다 우리가 끝낸다.
          setTimeout(settle, 300);
        };

        // onend 는 성공이든 실패든 마지막에 온다. 여기 한 곳에서만 끝낸다.
        recognition.onend = settle;

        try {
          recognition.start();
        } catch (error) {
          errorCode = "start_failed";
          settle();
        }
      }),
    [SpeechRecognitionClass, stopListening]
  );

  return { isSupported, listen, finishListening, stopListening };
};

/**
 * 받아쓰기가 자주 헷갈리는 짝. **소리가 같으면 같은 것으로 본다.**
 *
 * 브라우저 음성인식은 앞뒤 문맥으로 후보를 좁히는데, 1음절짜리 기능어는 문맥이
 * 거의 없어 후보가 너무 많다. `they` 가 `day`, `bee` 가 `be` 로 적히는 식이다.
 * `insects` 처럼 음절이 많은 낱말은 후보가 적어 잘 잡히므로 손댈 필요가 없다.
 *
 * **끊어읽기 단위에서 특히 크다.** `Bees /` 만 따로 읽히면 문맥이 아예 없고,
 * 짧은 덩어리라 한 낱말만 놓쳐도 점수가 절반으로 떨어진다.
 *
 * 왼쪽이 대표형이고 오른쪽이 그렇게 적히곤 하는 것들이다. 견줄 때 양쪽 다
 * 대표형으로 접어서 본다. **뜻이 다른 낱말을 억지로 묶지 말 것.** 아이가 정말
 * 틀리게 읽은 것까지 맞다고 해 버리면 채점이 없는 것과 같다.
 */
/**
 * 줄임말과 풀어 쓴 말은 같은 소리다. `You're` 를 받아쓰기가 `you are` 로 적는 일이
 * 잦은데, 그대로 견주면 아이가 똑같이 읽고도 두 낱말이 통째로 어긋난다. 실제로
 * "You're probably going to see them go" 를 그렇게 적으면 100점이 80점이 됐다.
 *
 * **양쪽 다 풀어 쓴 쪽으로 맞춘다.** 어느 쪽으로 들리든 같아진다.
 */
const CONTRACTION_MAP = new Map([
  ["i'm", "i am"],
  ["you're", "you are"],
  ["we're", "we are"],
  ["they're", "they are"],
  ["he's", "he is"],
  ["she's", "she is"],
  ["it's", "it is"],
  ["that's", "that is"],
  ["there's", "there is"],
  ["what's", "what is"],
  ["who's", "who is"],
  ["let's", "let us"],
  ["i've", "i have"],
  ["you've", "you have"],
  ["we've", "we have"],
  ["they've", "they have"],
  ["i'll", "i will"],
  ["you'll", "you will"],
  ["we'll", "we will"],
  ["they'll", "they will"],
  ["he'll", "he will"],
  ["she'll", "she will"],
  ["i'd", "i would"],
  ["you'd", "you would"],
  ["don't", "do not"],
  ["doesn't", "does not"],
  ["didn't", "did not"],
  ["isn't", "is not"],
  ["aren't", "are not"],
  ["wasn't", "was not"],
  ["weren't", "were not"],
  ["can't", "can not"],
  ["cannot", "can not"],
  ["couldn't", "could not"],
  ["won't", "will not"],
  ["wouldn't", "would not"],
  ["shouldn't", "should not"],
  ["haven't", "have not"],
  ["hasn't", "has not"],
  ["hadn't", "had not"],
]);

const HOMOPHONE_GROUP_LIST = [
  ["they", "day", "dey", "the", "thay"],
  ["their", "there", "they're", "dare", "der"],
  ["bee", "be", "b", "bea", "bees", "bes"],
  ["a", "uh", "ah", "eh"],
  ["an", "and", "en"],
  ["in", "en", "inn"],
  ["is", "as", "iz"],
  ["it", "eat", "id"],
  ["to", "too", "two", "tu"],
  ["for", "four", "fore"],
  ["of", "off", "ov"],
  ["are", "our", "r"],
  ["you", "u", "yo"],
  ["i", "ai", "eye"],
  ["he", "hee", "e"],
  ["we", "wee", "oui"],
  ["that", "dat"],
  ["this", "dis"],
  ["with", "wit", "width"],
  ["them", "dem"],
  ["then", "than", "den"],
  ["its", "it's"],
  ["one", "won"],
  ["no", "know"],
  ["by", "buy", "bye"],
];

// 헷갈리는 낱말 → 대표형. 위 목록에서 한 번만 만든다.
const HOMOPHONE_MAP = new Map(
  HOMOPHONE_GROUP_LIST.flatMap((group) =>
    group.map((word) => [word, group[0]])
  )
);

/** 소리가 같은 낱말을 대표형으로 접는다. 목록에 없으면 그대로 둔다. */
const foldHomophone = (word) => HOMOPHONE_MAP.get(word) || word;

/** 견주기 좋게 다듬는다. 문장부호와 대소문자는 발음과 상관이 없다. */
export const toComparableWordList = (text) =>
  (text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s']/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    // 줄임말은 풀어 쓴 쪽으로 맞춘다. 한 낱말이 둘로 늘기도 한다.
    .flatMap((word) => (CONTRACTION_MAP.get(word) || word).split(" "))
    .map(foldHomophone);

/**
 * 두 낱말이 한 글자 차이인가. 받아쓰기가 흘려 적은 것을 붙잡으려는 것이다.
 * `certain` 을 `sertan` 으로, `insects` 를 `insect` 로 적는 식이다.
 *
 * **짧은 낱말에는 쓰지 않는다.** 세 글자 이하는 한 글자만 달라도 뜻이 통째로
 * 바뀌어서(`cat`/`cut`, `big`/`bag`), 틀리게 읽은 것까지 맞다고 해 버린다.
 */
const MINIMUM_FUZZY_LENGTH = 5;

const isNearlySameWord = (left, right) => {
  if (left === right) return true;
  if (left.length < MINIMUM_FUZZY_LENGTH && right.length < MINIMUM_FUZZY_LENGTH)
    return false;
  if (Math.abs(left.length - right.length) > 1) return false;

  // 앞에서부터, 뒤에서부터 같은 데까지 걷어내고 남은 것이 한 글자 이하면 통과다.
  let head = 0;
  while (
    head < left.length &&
    head < right.length &&
    left[head] === right[head]
  )
    head++;

  let tail = 0;
  while (
    tail < left.length - head &&
    tail < right.length - head &&
    left[left.length - 1 - tail] === right[right.length - 1 - tail]
  )
    tail++;

  return left.length - head - tail <= 1 && right.length - head - tail <= 1;
};

/**
 * 읽어야 할 문장과 실제로 읽은 것이 얼마나 맞는지를 0~100 으로 준다.
 *
 * 순서를 지키며 겹치는 가장 긴 수열(LCS)을 세되, **겹친 만큼만 보면 안 된다.**
 * 그것만 보면 원문 단어가 다 들어 있기만 하면 만점이라, 아이가 뒤 문장까지 죽
 * 읽어 버려도 100 점이 나온다(실제로 그랬다). 그래서 두 가지를 함께 본다.
 *
 * - 재현율: 원문 중 몇 개를 읽었나 (빠뜨리면 깎인다)
 * - 정밀도: 읽은 것 중 몇 개가 원문이었나 (군더더기를 붙이면 깎인다)
 *
 * 둘의 조화평균(F1)이라 한쪽만 좋아서는 점수가 안 나온다. "uh" 하나 더듬는 정도는
 * 거의 안 깎이고, 문장 하나를 통째로 더 읽으면 절반 아래로 떨어진다.
 */
export const calculateReadingAccuracy = (expectedText, spokenText) => {
  const expectedWordList = toComparableWordList(expectedText);
  const spokenWordList = toComparableWordList(spokenText);

  if (expectedWordList.length === 0) return 0;
  if (spokenWordList.length === 0) return 0;

  const matchTable = Array.from({ length: expectedWordList.length + 1 }, () =>
    new Array(spokenWordList.length + 1).fill(0)
  );

  for (let i = 1; i <= expectedWordList.length; i++) {
    for (let j = 1; j <= spokenWordList.length; j++) {
      matchTable[i][j] = isNearlySameWord(
        expectedWordList[i - 1],
        spokenWordList[j - 1]
      )
        ? matchTable[i - 1][j - 1] + 1
        : Math.max(matchTable[i - 1][j], matchTable[i][j - 1]);
    }
  }

  const matchedCount = matchTable[expectedWordList.length][spokenWordList.length];
  if (matchedCount === 0) return 0;

  const recall = matchedCount / expectedWordList.length;
  const precision = matchedCount / spokenWordList.length;
  return Math.round(((2 * recall * precision) / (recall + precision)) * 100);
};

/** 분당 단어 수. 유창성의 대용이다. 억양까지 보지는 못한다. */
export const calculateWordsPerMinute = (spokenText, elapsedMilliseconds) => {
  const wordCount = toComparableWordList(spokenText).length;
  if (!wordCount || !elapsedMilliseconds) return 0;
  return Math.round(wordCount / (elapsedMilliseconds / 60000));
};
